/**
 * Processamento de Pagamentos CNAB/API
 * (prototype: erpone_processamento_de_pagamentos_cnab_api)
 *
 * Contratos:
 *   GET  /finance/payments?page&pageSize&statusIn&channel&q
 *          → PaginatedResponseDto<ScheduledPaymentResponseDto>
 *   POST /finance/payments                          → agendar (SchedulePaymentDto)
 *   POST /finance/payments/:id/transmit             → transmitir (TransmitPaymentDto { batchId })
 *   POST /finance/payments/:id/confirm              → confirmar entrada financeira
 *                                                    (ConfirmFinancialEntryDto { accountId, notes? })
 *
 * Campos reais do item (treasury.dto.ts — ScheduledPaymentResponseDto):
 *   id, companyId, payableId?, batchId?, beneficiaryName, beneficiaryDocument?,
 *   documentNumber?, dueDate, amount, paymentMethod, channel (CNAB|API),
 *   status (SCHEDULED|TRANSMITTED|CONFIRMED|FAILED|CANCELLED), log[], ...
 *
 * O protótipo mostra "Gerar Arquivo CNAB" e "Transmitir via API" globais; o
 * backend expõe transmissão POR PAGAMENTO (`POST :id/transmit`), então o botão
 * de ação fica na linha. Agendar exige `companyId`: usa a primeira empresa do
 * tenant (padrão de features/organization/useOrganization, mesmo de
 * features/accounting/useAccounting.useAccountingContext).
 */
import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  LoadingSkeleton,
  Modal,
  Input,
  Select,
} from '@nexora/design-system';
import { useAuth } from '../../../shared/auth/AuthContext';
import { useCompanies } from '../../organization/useOrganization';
import { useBankAccounts } from '../useBankAccounts';
import {
  useScheduledPayments,
  useSchedulePayment,
  useTransmitPayment,
  useConfirmFinancialEntry,
} from './treasuryHooks';
import {
  PAYMENT_CHANNEL_LABEL,
  PAYMENT_STATUS_LABEL,
  type PaymentStatus,
  type ScheduledPayment,
} from './treasuryTypes';
import { formatCurrency, formatDate, formatDateTime } from '../../../shared/utils/format';

/* ── Status → badge ── */

const STATUS_META: Record<PaymentStatus, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
  SCHEDULED: { variant: 'info', label: PAYMENT_STATUS_LABEL.SCHEDULED },
  TRANSMITTED: { variant: 'warning', label: PAYMENT_STATUS_LABEL.TRANSMITTED },
  CONFIRMED: { variant: 'success', label: PAYMENT_STATUS_LABEL.CONFIRMED },
  FAILED: { variant: 'error', label: PAYMENT_STATUS_LABEL.FAILED },
  CANCELLED: { variant: 'neutral', label: PAYMENT_STATUS_LABEL.CANCELLED },
};

const MONEY_CELL = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontVariantNumeric: 'tabular-nums' as const,
  textAlign: 'right' as const,
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'SCHEDULED', label: 'Agendado' },
  { value: 'TRANSMITTED', label: 'Transmitido' },
  { value: 'CONFIRMED', label: 'Confirmado' },
  { value: 'FAILED', label: 'Falhou' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

const CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'Todos os canais' },
  { value: 'CNAB', label: 'CNAB' },
  { value: 'API', label: 'API' },
];

/** Última entrada do log, para a coluna "Status / Log" da tabela. */
function lastLogEntry(p: ScheduledPayment): { at: string; event: string; message?: string } | null {
  const last = p.log?.[p.log.length - 1];
  return last ?? null;
}

export function PaymentsCnabPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { data: companies } = useCompanies(tenantId);
  const { data: accounts } = useBankAccounts();

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [channelFilter, setChannelFilter] = useState('ALL');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useScheduledPayments({
    page,
    pageSize: 20,
    statusIn: statusFilter === 'ALL' ? undefined : [statusFilter],
    channel: channelFilter === 'ALL' ? undefined : channelFilter,
  });

  const scheduleMutation = useSchedulePayment();
  const transmitMutation = useTransmitPayment();
  const confirmMutation = useConfirmFinancialEntry();

  /* Modais */
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    beneficiaryName: '',
    beneficiaryDocument: '',
    documentNumber: '',
    dueDate: '',
    amount: '',
    paymentMethod: 'PIX',
    channel: 'CNAB' as 'CNAB' | 'API',
  });
  const [transmitTarget, setTransmitTarget] = useState<ScheduledPayment | null>(null);
  const [batchId, setBatchId] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<ScheduledPayment | null>(null);
  const [confirmAccountId, setConfirmAccountId] = useState('');
  const [logTarget, setLogTarget] = useState<ScheduledPayment | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const payments = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  const companyId = companies?.[0]?.id ?? '';

  const totalScheduled = useMemo(
    () => payments.filter((p) => p.status === 'SCHEDULED').reduce((s, p) => s + p.amount, 0),
    [payments],
  );
  const pendingCount = useMemo(
    () => payments.filter((p) => p.status === 'SCHEDULED' || p.status === 'TRANSMITTED').length,
    [payments],
  );

  const showToast = (message: string, variant: 'success' | 'error'): void => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSchedule = async (): Promise<void> => {
    if (!companyId) {
      showToast('Nenhuma empresa cadastrada para agendar o pagamento.', 'error');
      return;
    }
    if (!scheduleForm.beneficiaryName || !scheduleForm.dueDate || !scheduleForm.amount) {
      showToast('Preencha beneficiário, vencimento e valor.', 'error');
      return;
    }
    try {
      await scheduleMutation.mutateAsync({
        companyId,
        beneficiaryName: scheduleForm.beneficiaryName,
        beneficiaryDocument: scheduleForm.beneficiaryDocument || undefined,
        documentNumber: scheduleForm.documentNumber || undefined,
        dueDate: new Date(scheduleForm.dueDate).toISOString(),
        amount: Number(scheduleForm.amount),
        paymentMethod: scheduleForm.paymentMethod,
        channel: scheduleForm.channel,
      });
      showToast('Pagamento agendado com sucesso.', 'success');
      setShowSchedule(false);
      setScheduleForm({
        beneficiaryName: '',
        beneficiaryDocument: '',
        documentNumber: '',
        dueDate: '',
        amount: '',
        paymentMethod: 'PIX',
        channel: 'CNAB',
      });
      setPage(1);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao agendar pagamento.', 'error');
    }
  };

  const handleTransmit = async (): Promise<void> => {
    if (!transmitTarget) return;
    try {
      await transmitMutation.mutateAsync({
        id: transmitTarget.id,
        batchId: batchId.trim() || `LOTE-${Date.now()}`,
      });
      showToast('Pagamento transmitido com sucesso.', 'success');
      setTransmitTarget(null);
      setBatchId('');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao transmitir pagamento.', 'error');
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (!confirmTarget) return;
    if (!confirmAccountId) {
      showToast('Selecione a conta financeira para a liquidação.', 'error');
      return;
    }
    try {
      await confirmMutation.mutateAsync({ id: confirmTarget.id, input: { accountId: confirmAccountId } });
      showToast('Entrada financeira confirmada — baixa efetuada quando vinculada a título.', 'success');
      setConfirmTarget(null);
      setConfirmAccountId('');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Erro ao confirmar pagamento.', 'error');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      {/* ── Page header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Processamento de Pagamentos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Gerencie remessas CNAB e transmissões via API bancária.
          </p>
        </div>
        <Button onClick={() => setShowSchedule(true)}>+ Novo Pagamento</Button>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-md mb-lg">
        <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md flex flex-col justify-between">
          <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Total Agendado (página)</span>
          <div className="font-display-lg text-display-lg text-primary mt-sm tabular-nums">{formatCurrency(totalScheduled)}</div>
          <div className="font-body-sm text-body-sm text-on-surface-variant mt-xs">
            {pendingCount} {pendingCount === 1 ? 'lote pendente' : 'lotes pendentes'} de envio
          </div>
        </div>
        <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md flex flex-col justify-between">
          <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Pagamentos na página</span>
          <div className="font-headline-md text-headline-md text-on-surface mt-sm tabular-nums">{payments.length}</div>
          <div className="font-body-sm text-body-sm text-on-surface-variant mt-xs">{total} no total (servidor)</div>
        </div>
        <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md flex flex-col justify-between">
          <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Canais</span>
          <div className="flex gap-sm mt-sm">
            {(['CNAB', 'API'] as const).map((c) => (
              <Badge key={c} variant="info" size="sm">{c}</Badge>
            ))}
          </div>
          <div className="font-body-sm text-body-sm text-on-surface-variant mt-xs">Transmissão por pagamento</div>
        </div>
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <Card padding="2rem">
          <LoadingSkeleton count={4} height="48px" />
          <p style={{ color: '#43474e', fontSize: '0.875rem' }}>Carregando pagamentos agendados...</p>
        </Card>
      )}

      {/* ── Error ── */}
      {error && !isLoading && (
        <Card padding="2rem" className="text-centered">
          <p style={{ color: '#ba1a1a', fontWeight: 600, margin: 0 }}>Erro ao carregar pagamentos</p>
          <p style={{ fontSize: '0.875rem', color: '#74777f' }}>{(error as Error).message}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
        </Card>
      )}

      {/* ── Empty honesto ── */}
      {!isLoading && !error && payments.length === 0 && (
        <Card padding="2rem" className="text-centered muted">
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            Nenhum pagamento agendado{statusFilter !== 'ALL' || channelFilter !== 'ALL' ? ' com os filtros atuais' : ''}.
          </p>
        </Card>
      )}

      {/* ── Tabela ── */}
      {!isLoading && !error && payments.length > 0 && (
        <Card variant="outlined" padding="0" className="overflow-hidden">
          <CardHeader className="px-md pt-md" action={
            <div className="flex flex-wrap items-center gap-sm">
              <select
                className="tr-filter"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                className="tr-filter"
                value={channelFilter}
                onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
              >
                {CHANNEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Pagamentos Agendados</h3>
          </CardHeader>
          <div className="tr-table-wrap">
            <table className="tr-table">
              <thead>
                <tr>
                  <th>Fornecedor / Beneficiário</th>
                  <th>Documento</th>
                  <th>Vencimento</th>
                  <th className="text-right">Valor (R$)</th>
                  <th>Método</th>
                  <th>Status / Log</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const last = lastLogEntry(p);
                  const meta = STATUS_META[p.status] ?? { variant: 'neutral' as const, label: p.status };
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="tr-cell-strong">{p.beneficiaryName}</div>
                        <div className="tr-cell-mono">{p.beneficiaryDocument ?? '—'}</div>
                      </td>
                      <td className="tr-cell-mono">{p.documentNumber ?? '—'}</td>
                      <td>{formatDate(p.dueDate)}</td>
                      <td className="tr-cell-money" style={MONEY_CELL}>{formatCurrency(p.amount)}</td>
                      <td>
                        <div className="flex items-center gap-xs">
                          <span>{p.paymentMethod}</span>
                          <Badge variant={p.channel === 'API' ? 'info' : 'neutral'} size="sm">
                            {PAYMENT_CHANNEL_LABEL[p.channel] ?? p.channel}
                          </Badge>
                        </div>
                      </td>
                      <td>
                        <Badge variant={meta.variant} size="sm" dot>{meta.label}</Badge>
                        {last && (
                          <div className="tr-cell-muted tr-cell-log" title={`${last.event}${last.message ? ` — ${last.message}` : ''}`}>
                            {last.event}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="tr-actions">
                          <Button size="sm" variant="ghost" onClick={() => setLogTarget(p)}>Log</Button>
                          {p.status === 'SCHEDULED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setTransmitTarget(p); setBatchId(''); }}
                              loading={transmitMutation.isPending && transmitTarget?.id === p.id}
                            >
                              Transmitir
                            </Button>
                          )}
                          {p.status === 'TRANSMITTED' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setConfirmTarget(p); setConfirmAccountId(''); }}
                              loading={confirmMutation.isPending && confirmTarget?.id === p.id}
                            >
                              Confirmar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Paginação */}
          <div className="tr-pagination">
            <span className="tr-pagination-info">
              Mostrando {payments.length} de {total} registro(s)
            </span>
            <div className="flex gap-sm">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Anterior
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Próxima
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── Modal: Novo pagamento ── */}
      <Modal
        isOpen={showSchedule}
        onClose={() => setShowSchedule(false)}
        title="Agendar Pagamento"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowSchedule(false)}>Cancelar</Button>
            <Button onClick={() => void handleSchedule()} loading={scheduleMutation.isPending}>Agendar</Button>
          </>
        }
      >
        <div className="tr-form">
          <Input
            label="Beneficiário *"
            value={scheduleForm.beneficiaryName}
            onChange={(e) => setScheduleForm({ ...scheduleForm, beneficiaryName: e.target.value })}
            placeholder="Razão social / nome"
          />
          <div className="tr-form__row">
            <Input
              label="CPF/CNPJ"
              value={scheduleForm.beneficiaryDocument}
              onChange={(e) => setScheduleForm({ ...scheduleForm, beneficiaryDocument: e.target.value })}
            />
            <Input
              label="Documento de origem"
              value={scheduleForm.documentNumber}
              onChange={(e) => setScheduleForm({ ...scheduleForm, documentNumber: e.target.value })}
              placeholder="NF-e 1234"
            />
          </div>
          <div className="tr-form__row">
            <Input
              label="Vencimento *"
              type="date"
              value={scheduleForm.dueDate}
              onChange={(e) => setScheduleForm({ ...scheduleForm, dueDate: e.target.value })}
            />
            <Input
              label="Valor (R$) *"
              type="number"
              step="0.01"
              min="0.01"
              value={scheduleForm.amount}
              onChange={(e) => setScheduleForm({ ...scheduleForm, amount: e.target.value })}
            />
          </div>
          <div className="tr-form__row">
            <Select
              label="Método"
              value={scheduleForm.paymentMethod}
              onChange={(e) => setScheduleForm({ ...scheduleForm, paymentMethod: e.target.value })}
            >
              <option value="PIX">Pix</option>
              <option value="TED">TED</option>
              <option value="BOLETO">Boleto</option>
              <option value="TRANSFER">Transferência</option>
            </Select>
            <Select
              label="Canal"
              value={scheduleForm.channel}
              onChange={(e) => setScheduleForm({ ...scheduleForm, channel: e.target.value as 'CNAB' | 'API' })}
            >
              <option value="CNAB">CNAB</option>
              <option value="API">API</option>
            </Select>
          </div>
          <p className="tr-form__hint">
            Empresa usada no agendamento: <strong>{companies?.[0]?.name ?? '—'}</strong> (primeira do tenant).
          </p>
        </div>
      </Modal>

      {/* ── Modal: Transmitir ── */}
      <Modal
        isOpen={!!transmitTarget}
        onClose={() => setTransmitTarget(null)}
        title="Transmitir Pagamento"
        footer={
          <>
            <Button variant="ghost" onClick={() => setTransmitTarget(null)}>Cancelar</Button>
            <Button onClick={() => void handleTransmit()} loading={transmitMutation.isPending}>Transmitir</Button>
          </>
        }
      >
        <p style={{ fontSize: '0.875rem', color: '#43474e', margin: 0 }}>
          Transmite <strong>{transmitTarget?.beneficiaryName}</strong> ({formatCurrency(transmitTarget?.amount ?? 0)}) ao banco/API
          {transmitTarget?.channel === 'CNAB' ? ' — gera lote CNAB' : ' — gera requisição API'}.
        </p>
        <div style={{ marginTop: '1rem' }}>
          <Input
            label="Identificador do lote (batchId)"
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            placeholder={`LOTE-${Date.now()}`}
          />
        </div>
      </Modal>

      {/* ── Modal: Confirmar liquidação ── */}
      <Modal
        isOpen={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        title="Confirmar Entrada Financeira"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmTarget(null)}>Cancelar</Button>
            <Button onClick={() => void handleConfirm()} loading={confirmMutation.isPending}>Confirmar</Button>
          </>
        }
      >
        <p style={{ fontSize: '0.875rem', color: '#43474e', margin: 0 }}>
          Confirma a liquidação de <strong>{confirmTarget?.beneficiaryName}</strong>. Quando vinculado a um título,
          a baixa automática é efetuada.
        </p>
        <div style={{ marginTop: '1rem' }}>
          <Select label="Conta financeira (débito) *" value={confirmAccountId} onChange={(e) => setConfirmAccountId(e.target.value)}>
            <option value="">Selecione a conta</option>
            {(accounts ?? []).map((acc) => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </Select>
        </div>
      </Modal>

      {/* ── Modal: Log ── */}
      <Modal
        isOpen={!!logTarget}
        onClose={() => setLogTarget(null)}
        title={`Log — ${logTarget?.beneficiaryName ?? ''}`}
        footer={<Button variant="ghost" onClick={() => setLogTarget(null)}>Fechar</Button>}
      >
        {logTarget && (
          <div>
            <div className="flex gap-md mb-md flex-wrap">
              <div className="tr-log-stat">
                <span className="tr-log-stat__label">Valor</span>
                <span className="tr-log-stat__value">{formatCurrency(logTarget.amount)}</span>
              </div>
              <div className="tr-log-stat">
                <span className="tr-log-stat__label">Vencimento</span>
                <span className="tr-log-stat__value">{formatDate(logTarget.dueDate)}</span>
              </div>
              <div className="tr-log-stat">
                <span className="tr-log-stat__label">Canal</span>
                <span className="tr-log-stat__value">{logTarget.channel}</span>
              </div>
              <div className="tr-log-stat">
                <span className="tr-log-stat__label">Status</span>
                <span className="tr-log-stat__value">{PAYMENT_STATUS_LABEL[logTarget.status] ?? logTarget.status}</span>
              </div>
            </div>
            <div className="tr-log-list">
              {logTarget.log.length === 0 && (
                <p style={{ fontSize: '0.875rem', color: '#74777f', margin: 0 }}>Sem eventos registrados.</p>
              )}
              {logTarget.log.map((entry, idx) => (
                <div key={idx} className="tr-log-entry">
                  <div className="tr-log-entry__when">{formatDateTime(entry.at)}</div>
                  <div className="tr-log-entry__event">
                    <strong>{entry.event}</strong>
                    {entry.message && <div style={{ fontSize: '0.8125rem', color: '#43474e' }}>{entry.message}</div>}
                  </div>
                </div>
              ))}
            </div>
            {logTarget.batchId && (
              <p className="tr-form__hint" style={{ marginTop: '1rem' }}>Lote: <strong>{logTarget.batchId}</strong></p>
            )}
          </div>
        )}
      </Modal>

      {/* ── Toast ── */}
      {toast && (
        <div role="alert" className="tr-toast" style={{
          background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
          border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
          color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
        }}>
          {toast.message}
          <button onClick={() => setToast(null)} aria-label="Fechar" className="tr-toast__close">&times;</button>
        </div>
      )}

      <style>{`
        .text-centered { text-align: center; }
        .muted { color: #74777f; }
        .tr-filter {
          padding: 0.375rem 0.5rem; border: 1px solid #c4c6cf; border-radius: 4px;
          font-size: 0.8125rem; background: #ffffff; color: #191c1e; height: 34px;
        }
        .tr-table-wrap { overflow-x: auto; }
        .tr-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .tr-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase;
          letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5;
          text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .tr-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; vertical-align: top; }
        .tr-table tbody tr:hover td { background: #eceef0; }
        .tr-cell-strong { font-weight: 600; }
        .tr-cell-muted { color: #43474e; }
        .tr-cell-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.8125rem; color: #43474e; }
        .tr-cell-money { font-weight: 600; color: #002045; }
        .tr-cell-log { font-size: 0.75rem; margin-top: 0.25rem; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .tr-actions { display: flex; gap: 0.25rem; justify-content: center; flex-wrap: wrap; }
        .tr-pagination {
          display: flex; justify-content: space-between; align-items: center;
          padding: 0.75rem 1rem; border-top: 1px solid #eceef0; font-size: 0.8125rem; color: #43474e;
        }
        .tr-form { display: flex; flex-direction: column; gap: 1rem; min-width: 420px; }
        .tr-form__row { display: flex; gap: 0.75rem; }
        .tr-form__row > * { flex: 1; }
        .tr-form__hint { margin: 0; font-size: 0.8125rem; color: #74777f; }
        .tr-log-stat { flex: 1; min-width: 110px; padding: 0.5rem 0.75rem; background: #f2f4f6; border-radius: 4px; }
        .tr-log-stat__label { display: block; font-size: 0.6875rem; color: #43474e; text-transform: uppercase; letter-spacing: 0.05em; }
        .tr-log-stat__value { font-size: 0.875rem; font-weight: 600; color: #191c1e; margin-top: 0.25rem; display: block; }
        .tr-log-list { display: flex; flex-direction: column; gap: 0.75rem; border-left: 2px solid #e0e3e5; padding-left: 1rem; }
        .tr-log-entry__when { font-size: 0.75rem; color: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .tr-log-entry__event { font-size: 0.875rem; color: #191c1e; }
        .tr-toast {
          position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem;
          border-radius: 8px; font-size: 0.875rem; font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 400px;
        }
        .tr-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
      `}</style>
    </div>
  );
}